import { DataTable, DataTableColumn, DataTableSortStatus, useDataTableColumns } from "mantine-datatable";
import { Tooltip, Text, Box, Menu, Group } from "@mantine/core";
import { TextCell, TextCellSkeleton } from "../components/TableCells/TextCell";
import { TitleLinkCell, TitleLinkCellSkeleton } from "../components/TableCells/TitleLinkCell";
import { OwnerCell, OwnerCellSkeleton } from "../components/TableCells/OwnerCell";
import { StatusCell, StatusCellSkeleton } from "../components/TableCells/StatusCell";
import { PriorityCell, PriorityCellSkeleton } from "../components/TableCells/PriorityCell";
import { TemplateCell, TemplateCellSkeleton } from "../components/TableCells/TemplateCell";
import { CommitmentsCell, CommitmentsCellSkeleton } from "../components/TableCells/CommitmentsCell";
import { DynamicCell, DynamicCellSkeleton } from "../components/TableCells/DynamicCell";
import { updateAccessorByState } from "../../../utils";
import { CLASS_NAMES } from "../constants";
import { buildIdeaUrlWithEvidenceFilters } from "../../../utils/ideasUrlState";
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { IconPencil, IconEyeOff, IconArrowUp, IconArrowDown } from '@tabler/icons';
import { useNavigate } from "react-router-dom";
import StarCell, { StarCellSkeleton } from "../components/TableCells/StarCell";
import NewRequestsCell, { NewRequestsCellSkeleton } from "../components/TableCells/NewRequestsCell";
import { TagsCell, TagsCellSkeleton } from "../components/TableCells/TagsCell";
import { ReleaseCell, ReleaseCellSkeleton } from "../components/TableCells/ReleaseCell";
import { BadgesCell, BadgesCellSkeleton } from "../components/TableCells/BadgesCell";
import { CustomersCell, CustomersCellSkeleton } from "../components/TableCells/CustomersCell";
import { EditableDateCell, EditableDateCellSkeleton } from "../components/TableCells/EditableDateCell";
import { useHover } from "@mantine/hooks";
import { DeliveryStatusCell, DeliveryStatusCellSkeleton } from "../components/TableCells/DeliveryStatusCell";
import { BarCell, BarCellSkeleton } from "../components/TableCells/bar/BarCell";
import { calcTopValues } from "../ideasHelpers";
import FiltersContext from "../filters/context/FiltersContext";
import {useKnowledgeData} from "../../../hooks/useKnowledgeData";
import { useQueryClient } from "@tanstack/react-query";
import { ideaKeys } from "../../../hooks/ideaPage/ideaKeys";
import {useFields} from "../../../hooks/useFields";
import { SortArrow } from "./SortArrow";
import _ from "lodash";
import { isAiView } from "../../../utils/viewUtils";
import {
  IDEA_COLUMN_MAX_WIDTH_PX,
  IDEA_COLUMN_WIDTH_PX,
  clampIdeaColumnWidth,
  exceedsIdeaColumnMaxWidth,
} from "../components/columnWidthOptions";


// Helper function to check if there are active evidence filters
const hasActiveEvidenceFilters = (activeFilters: any[]): boolean => {
  return activeFilters?.some(filter => filter.filterObject === 'evidence') || false;
};

// Helper function to check if a column should remain sortable when evidence filters are active
const shouldRemainSortable = (accessor: string): boolean => {
  // Only allow sorting for aggregations.accountsCount.(assigned|suggested|total) columns
  return accessor?.match(/^aggregations\.accountsCount\.(assigned|suggested|total)$/) !== null;
};

export default function IdeasTable({
  ideaColumns,
  setEditColumnModal,
  updateColumn,
  ideas,
  isAllowedToEditDates,
  handlePageChange,
  totalRecords,
  recordsPerPage,
  page,
  sortStatus,
  setSortStatus,
  loading,
  aggregationsLoading = false,
  PAGE_SIZE,
  selectedView,
  isTagsHovered = false,
  sortState = "total",
  isAiPage = false,
  searchQuery = "",
  onColumnsReordered,
}: {
  ideaColumns: any[];
  setEditColumnModal: (column: any, anchorRect?: DOMRect) => void;
  updateColumn: (updatedColumn: any) => void;
  ideas: any[];
  isAllowedToEditDates: boolean;
  handlePageChange: (newPage: string) => void;
  totalRecords: number | undefined;
  recordsPerPage: number;
  page: number;
  sortStatus: DataTableSortStatus;
  setSortStatus: React.Dispatch<React.SetStateAction<DataTableSortStatus>>;
  loading: boolean;
  aggregationsLoading?: boolean;
  PAGE_SIZE: number;
  selectedView?: any;
  isTagsHovered?: boolean;
  sortState?: string;
  isAiPage?: boolean;
  searchQuery?: string;
  onColumnsReordered?: (orderedAccessors: string[]) => void;
}) {
  const {
    requestPriorities,
    ideaPriorities,
    customEvidenceObj,
    generatedEnabled
  } = useFields();

  const { displayState, ideasTableDisplay } = useKnowledgeData();
  const { subPageKey, activeFilters } = useContext(FiltersContext);

  // Check if evidence filters are active
  const evidenceFiltersActive = hasActiveEvidenceFilters(activeFilters);
  
  // Debug logging (development only)
  if (process.env.NODE_ENV === 'development') {
    
  }

  const { ref } = useHover();

  const [selectedHeaderAccessor, setSelectedHeaderAccessor] = useState<string | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cellComponents: { [key: string]: React.FC<any> } = {
    TextCell,
    TitleLinkCell,
    OwnerCell,
    StatusCell,
    PriorityCell,
    TemplateCell,
    CommitmentsCell,
    StarCell,
    DynamicCell,
    NewRequestsCell,
    TagsCell,
    // The backend column registration names this exact string in
    // `renderComponent`; nothing type-checks that seam, so do not rename it.
    ReleaseCell,
    BadgesCell,
    DeliveryStatusCell,
    BarCell,
    CustomersCell,
    EditableDateCell,
  };

  // Skeleton components mapping
  const skeletonComponents: { [key: string]: React.FC<any> } = {
    TextCell: TextCellSkeleton,
    TitleLinkCell: TitleLinkCellSkeleton,
    OwnerCell: OwnerCellSkeleton,
    StatusCell: StatusCellSkeleton,
    PriorityCell: PriorityCellSkeleton,
    TemplateCell: TemplateCellSkeleton,
    CommitmentsCell: CommitmentsCellSkeleton,
    StarCell: StarCellSkeleton,
    DynamicCell: DynamicCellSkeleton,
    NewRequestsCell: NewRequestsCellSkeleton,
    TagsCell: TagsCellSkeleton,
    ReleaseCell: ReleaseCellSkeleton,
    BadgesCell: BadgesCellSkeleton,
    DeliveryStatusCell: DeliveryStatusCellSkeleton,
    BarCell: BarCellSkeleton,
    CustomersCell: CustomersCellSkeleton,
    EditableDateCell: EditableDateCellSkeleton,
  };

  const dealBreakerObj = requestPriorities.find(
    (r: any) => r.key === "Deal Breaker"
  ) || {
    key: "Deal Breaker",
    label: "Deal Breaker",
  };

  const handleOnRowClick = (ideaId: string) => {
    // Pre-populate the single idea cache with data from the list for instant navigation
    const currentIdea = ideas.find((idea: any) => idea._id === ideaId);

    // For theme rows (Bagel AI tab), navigate to the parent idea with the theme pre-selected.
    const isThemeRow = !!currentIdea?._isThemeRow;
    const targetIdeaId = isThemeRow && currentIdea?.parentIdeaId ? currentIdea.parentIdeaId : ideaId;
    const selectedThemeTitle = isThemeRow ? (currentIdea?.title || "") : "";

    if (currentIdea && !isThemeRow) {
      queryClient.setQueryData(ideaKeys.detail(targetIdeaId), currentIdea);
    }

    // Transform activityLog / itemChangeLog filters → createdAt date range BEFORE
    // building the URL so the idea page URL already carries the correct filter.
    // The URL effect in IdeaPageContext then handles routing it to trendFilters.
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const filtersToSave = (activeFilters || []).map((filter: any) => {
      const dbFieldPath: string = filter.dbFieldPath || '';
      const operator: string = filter.operator || '';
      const isLogFilter =
        dbFieldPath.startsWith('activityLog') ||
        dbFieldPath.startsWith('productItem.itemChangeLog');

      if (isLogFilter && currentIdea?.changeLogTimestamp) {
        return {
          ...filter,
          filterObject: 'evidence',
          dbFieldPath: 'createdAt',
          name: 'CreatedAt',
          label: 'Created At',
          type: 'date',
          recoverableDBFieldPath: dbFieldPath,
          recoverableOperator: operator,
          operator: 'custom',
          query: null,
          selected: [currentIdea.changeLogTimestamp, today.toISOString()],
        };
      }
      return filter;
    });

    // Build the URL with the (already-transformed) filters so the idea page
    // URL encodes createdAt directly and the URL effect applies it correctly.
    const url = buildIdeaUrlWithEvidenceFilters(
      targetIdeaId,
      location.search,
      filtersToSave,
      selectedThemeTitle ? { selectedTheme: selectedThemeTitle } : undefined,
    );

    const currentParams = new URLSearchParams(location.search);
    sessionStorage.setItem("lastFilters", `?${currentParams.toString()}`);
    sessionStorage.setItem("lastActiveFilters", JSON.stringify(filtersToSave));
    sessionStorage.setItem("lastActiveFiltersSubPage", subPageKey || "");
    if(subPageKey){
      sessionStorage.setItem("subPageKey", `${subPageKey}`);
    } else {
      sessionStorage.removeItem("subPageKey");
    }
  
    // Navigate to the new URL (works for both AI and regular idea pages).
    navigate(url);
  };

  const onSortStatusChanged = (sortStatus: DataTableSortStatus) => {
    setSortStatus(sortStatus);
  };

  const cols = useMemo(
    () => calcTopValues(ideas, ideaColumns),
    [ideaColumns, sortState, ideas, sortStatus]
  );

  // Mantine's useDataTableColumns persists column order/width/visibility per
  // storeKey. The AI view has a different column set (includes parentIdeaTitle,
  // drops most idea columns), so it needs its own persistence bucket —
  // otherwise switching tabs lets the idea-tab's stored order drop columns
  // that don't exist in it (e.g. parentIdeaTitle) on the next mount.
  const storeKey = isAiView(selectedView)
    ? 'ideas-table-bagel-ai'
    : `ideas-table-${subPageKey || 'all'}`;

  // Generate skeleton data using the same filtered columns as DataTable
  const skeletonData = useMemo(() => {
    if (!loading) return [];
    
    // Simple skeleton data - cells will be rendered as skeletons in the render method
    return Array.from({ length: 25 }, (_, index) => ({
      _id: `skeleton-${index}`,
      __isSkeletonRow: true, // Flag to identify skeleton rows
    }));
  }, [loading]);

  // Debug logging (development only)
  if (process.env.NODE_ENV === 'development') {
    // Log column details
    /* const enabledCols = cols.filter((col: any) => col.enabled && !col.hidden); */
  }

  const CustomHeader = ({ column }: { column: any }) => {
    const menuTargetRef = useRef<HTMLDivElement>(null);
    const labelContent = column?.title ?? '';
    const formattedAccessor = `${column.accessor}_${column.renderComponent}`;
    const isSelected = selectedHeaderAccessor === formattedAccessor;

    const updateColumnVisibility = () => {
      const currentColumn = _.find(ideaColumns, item => item.accessor === column.accessor && item.renderComponent === column.renderComponent);
      const updatedColumn = {
        ...currentColumn,
        hidden: true
      }
      updateColumn(updatedColumn);
    }

    const updateSorting = (columnAccessor: string, direction: 'asc' | 'desc') => {
      onSortStatusChanged({ columnAccessor, direction })
    }

    const openEditColumnModal = () => {
      const ideaColumn = _.find(ideaColumns, item => item.accessor === column.accessor && item.renderComponent === column.renderComponent);
      const anchorRect = menuTargetRef.current?.getBoundingClientRect();
      setEditColumnModal(ideaColumn, anchorRect ?? undefined);
    }

    const toggleMenu = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedHeaderAccessor((prev) => (prev === formattedAccessor ? null : formattedAccessor));
    };

    return (
      <Menu
        shadow="md"
        width={180}
        position="right-start"
        offset={0}
        opened={isSelected}
        onClose={() => setSelectedHeaderAccessor(null)}
      >
        <Menu.Target>
          <Box
            ref={menuTargetRef}
            onClick={toggleMenu}
            sx={{
              width: '100%',
              height: '100%',
              minHeight: 40,
              display: 'flex',
              alignItems: 'center',
              padding: '0.8rem 0.625rem',
              cursor: 'pointer',
              borderRadius: 0,
            }}
          >
            <Group
              position="left"
              noWrap
              style={{ width: '100%' }}
            >
              <Tooltip label={column?.tooltip} disabled={!column?.tooltip}>
                <Text className={CLASS_NAMES.columnHeaderLabel}>{labelContent}</Text>
              </Tooltip>
            </Group>
          </Box>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            icon={<IconPencil size={16} color={'grey'} />}
            onClick={ () => openEditColumnModal() }
          >
            Edit column
          </Menu.Item>
          <Menu.Item
            icon={<IconEyeOff size={16} color={'grey'} />}
            onClick={ () => updateColumnVisibility() }
          >
            Hide column
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            icon={<IconArrowUp size={16} color={'grey'} />}
            onClick={ () => updateSorting(column.accessor, 'asc') }
          >
            Sort ascending
          </Menu.Item>
          <Menu.Item
            icon={<IconArrowDown size={16} color={'grey'} />}
            onClick={ () => updateSorting(column.accessor, 'desc') }
          >
            Sort descending
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  };

  const visibleCols = useMemo(
    () =>
      cols
        .filter((col: any) => col.enabled && !col.hidden)
        .sort((a: any, b: any) => a.index - b.index),
    [cols]
  );

  // Stable accessor order derived from cheap data only -- used by sync effects
  // so they don't depend on the full columnDefs reference (which contains JSX/closures).
  const defsAccessorOrder = useMemo(
    () =>
      visibleCols.map((col: any) =>
        col.renderComponent === "DynamicCell" || col.renderComponent === "BarCell"
          ? updateAccessorByState(col.accessor, sortState)
          : col.accessor
      ),
    [visibleCols, sortState]
  );

  const columnDefs = useMemo(() => visibleCols.map((col: any, idx: number) => {
    const finalAccessor =
      col.renderComponent === "DynamicCell" || col.renderComponent === "BarCell"
        ? updateAccessorByState(col.accessor, sortState)
        : col.accessor;

    const isAggregationColumn = finalAccessor?.startsWith("aggregations.");
    const columnSortable =
      evidenceFiltersActive && isAggregationColumn
        ? shouldRemainSortable(finalAccessor)
          ? col?.sortable
          : false
        : col?.sortable;

    const isFirstColumn = idx === 0;
    const isLastColumn = idx === visibleCols.length - 1;

    // Only the AI tab's elastic absorber (last col) is non-resizable.
    const isElasticLastColumn = isAiView(selectedView) && isLastColumn;

    return {
      ...col,
      accessor: finalAccessor,
      _originalAccessor: col.accessor,
      ideaTableRenderComponent:
        col.accessor === "status" ? "StatusCell" : col.renderComponent,
      title: <CustomHeader column={col} />,
      // Normalize numeric widths to px strings — mantine-datatable inlines them as raw CSS,
      // and an invalid `width: 240 !important` is silently dropped under `table-layout: fixed`.
      width: typeof col.width === "number" ? `${col.width}px` : col.width,
      openIdeaOnCLick: col?.openIdeaOnCLick,
      sortable: columnSortable,
      draggable: !isFirstColumn && !isLastColumn,
      resizable: !isElasticLastColumn,
      render: (data: any) => {
        const renderKey =
          col.accessor === "status" ? "StatusCell" : col.renderComponent;
        const CellComponent = cellComponents[renderKey]
          ? cellComponents[renderKey]
          : cellComponents["TextCell"];

        const SkeletonComponent = skeletonComponents[renderKey]
          ? skeletonComponents[renderKey]
          : skeletonComponents["TextCell"];

        const isAggCol =
          col.accessor && col.accessor.includes("aggregations.");
        const shouldShowAggregationSkeleton =
          isAggCol && aggregationsLoading && !data.__isSkeletonRow;

        const renderData = {
          ...data,
          searchQuery:
            col.accessor === "title" && searchQuery ? searchQuery : undefined,
        };

        const isIdeaStatusColumn = col.accessor === "status";

        const align: "left" | "center" | "right" =
          col.textAlignment === "center" || col.textAlignment === "right"
            ? col.textAlignment
            : "left";
        const justifyContent =
          align === "center"
            ? "center"
            : align === "right"
              ? "flex-end"
              : "flex-start";

        return (
          <Box
            ref={ref}
            sx={{
              width: "100%",
              textAlign: align,
              // Cells wrap content in a Mantine Group or Flex (a flex container).
              // In Mantine v6, `Group` exposes `.mantine-Group-root` but `Flex`
              // is just a Box with `display: flex` (no class). Target both:
              //   - `& > *` covers cells whose ROOT is a Flex (PriorityCell etc.)
              //   - `& > * > .mantine-Group-root` covers cells whose root is a
              //     block Box wrapping a Group (TextCell, StatusCell, ...)
              // `justify-content` is silently ignored on non-flex elements, so
              // the broad `& > *` selector is a no-op where it doesn't apply.
              "& > *, & > * > .mantine-Group-root": { justifyContent },
            }}
            onMouseDown={
              isIdeaStatusColumn ? (e) => e.stopPropagation() : undefined
            }
            onClick={
              isIdeaStatusColumn ? (e) => e.stopPropagation() : undefined
            }
          >
            {data.__isSkeletonRow || shouldShowAggregationSkeleton ? (
              <SkeletonComponent col={col} />
            ) : (
              <CellComponent
                data={renderData}
                col={col}
                renderParams={col.renderParams || {}}
                ideaPriorities={ideaPriorities}
                dealBreakerObj={dealBreakerObj}
                customEvidenceObj={customEvidenceObj}
                isAllowedToEditDates={isAllowedToEditDates}
                ideasTableDisplay={ideasTableDisplay}
                displayState={displayState}
                accessor={col.accessor}
                sortState={sortState}
              />
            )}
          </Box>
        );
      },
    };
  }), [
    visibleCols, sortState, evidenceFiltersActive, selectedHeaderAccessor,
    selectedView,
    ideaColumns, updateColumn, setEditColumnModal, ref,
    aggregationsLoading, searchQuery, ideaPriorities, dealBreakerObj,
    customEvidenceObj, isAllowedToEditDates, ideasTableDisplay, displayState,
  ]);

  const {
    effectiveColumns,
    setColumnWidth,
    setColumnsOrder,
    columnsWidth,
    setColumnsWidth,
    columnsToggle,
    setColumnsToggle,
  } = useDataTableColumns({
    key: storeKey,
    columns: columnDefs,
  });

  // After a tab switch, mantine's `useDataTableColumns` lags by one
  // render: `columnsWidth`/`columnsToggle`/`effectiveColumns` still hold
  // the previous tab's data while the setters are already bound to the
  // new `storeKey`. Any additive merge in that window writes the old
  // tab's entries into the new tab's storage. Gate the merges below on
  // accessor-set parity between defs and effectiveColumns (same idea as
  // the order sync's setsMatch guard).
  const hookInSync = useMemo(() => {
    const eff = new Set(
      effectiveColumns.map((c: any) => c.accessor as string).filter(Boolean)
    );
    const defs = new Set(defsAccessorOrder);
    return eff.size === defs.size && defsAccessorOrder.every((a) => eff.has(a));
  }, [effectiveColumns, defsAccessorOrder]);

  // mantine-datatable's `useDataTableColumns` only seeds the persisted width
  // map (`columnsWidth`) on first mount from whatever columns existed THEN.
  // Any column that appears later — added by subPage configuration, returning
  // after a sortState toggle (DynamicCell/BarCell variants), or simply absent
  // from a pre-existing localStorage entry written when the column set was
  // smaller — never gets an entry. mantine's `setColumnWidth(I, g)` is
  // implemented as `m.map(C => C[I] ? {[I]:g} : C)`, which is a silent no-op
  // for accessors not already in the map. The column then appears
  // non-resizable even though `resizable: true` is set.
  //
  // Mirror the existing defs→setColumnsOrder sync below for widths: append a
  // default entry (`col.width ?? "initial"`, matching mantine's own seed) for
  // any accessor that's missing. Existing entries are NEVER overwritten, so
  // user-resized widths and EditColumnModal selections are preserved.
  useEffect(() => {
    if (defsAccessorOrder.length === 0) return;
    if (!hookInSync) return;
    const widthByAccessor = new Map<string, any>();
    for (const col of columnDefs) {
      widthByAccessor.set(col.accessor as string, (col as any).width);
    }
    // Use a functional updater so this always operates on the latest state.
    // Spreading a captured `columnsWidth` snapshot would let a concurrent
    // setColumnsWidth call (e.g. EditColumnModal sync below) silently drop
    // whichever update ran first.
    setColumnsWidth((prev) => {
      const known = new Set<string>(
        (prev || []).flatMap((entry: any) => Object.keys(entry))
      );
      const additions: any[] = [];
      for (const accessor of defsAccessorOrder) {
        if (accessor && !known.has(accessor)) {
          // Fall back to M (150 px) instead of "initial" so new columns start
          // at a sensible size and drag resize gives live visual feedback.
          const configuredWidth = widthByAccessor.get(accessor);
          additions.push({ [accessor]: configuredWidth ?? `${IDEA_COLUMN_WIDTH_PX.M}px` });
        }
      }
      return additions.length > 0 ? [...(prev || []), ...additions] : prev;
    });
  }, [defsAccessorOrder, columnDefs, setColumnsWidth, hookInSync]);

  // Same pattern as the width sync above, for `columnsToggle`. When
  // ValidatedSwitch flips sortState, DynamicCell/BarCell accessors get
  // rewritten (`.total` → `.assigned`); mantine's effectiveColumns then
  // resolves `!toggle.find(...)?.toggled` to `true` for the new accessor
  // and hides the column. Append a `toggled: true` entry for any missing
  // accessor; never overwrite existing entries (preserves user hides).
  useLayoutEffect(() => {
    if (defsAccessorOrder.length === 0) return;
    if (!hookInSync) return;
    const known = new Set<string>(
      (columnsToggle || []).map((t: any) => t.accessor)
    );
    const colByAccessor = new Map<string, any>();
    for (const col of columnDefs) {
      colByAccessor.set(col.accessor as string, col);
    }
    const additions: any[] = [];
    for (const accessor of defsAccessorOrder) {
      if (accessor && !known.has(accessor)) {
        const col = colByAccessor.get(accessor);
        additions.push({
          accessor,
          defaultToggle: true,
          toggleable: !!col?.toggleable,
          toggled: true,
        });
      }
    }
    if (additions.length > 0) {
      setColumnsToggle([...(columnsToggle || []), ...additions]);
    }
  }, [defsAccessorOrder, columnDefs, columnsToggle, setColumnsToggle, hookInSync]);

  // Make the last column on the Bagel AI tab elastic so it absorbs any
  // leftover horizontal space. Without this, auto table-layout redistributes
  // that slack across user-resizable columns and amplifies the rendered width
  // above what mantine-datatable's resize handler asks for, causing the
  // column edge to run ahead of the cursor (the original AI-tab resize bug).
  // With the last column auto-sized, sibling resizes only change the elastic
  // column's width — user-resizable columns get exactly what mantine sets,
  // and the browser's natural content-based min-width floor is preserved
  // (same UX as the regular tab). The last AI column is already
  // non-resizable (`resizable: !isLastColumn`), so the user wasn't dragging
  // it directly. The width !== 'initial' guard makes this a no-op after the
  // first run.
  useEffect(() => {
    if (!isAiView(selectedView) || effectiveColumns.length === 0) return;
    const last: DataTableColumn<any> = effectiveColumns[effectiveColumns.length - 1];
    if (last?.accessor && last.width !== "initial") {
      setColumnWidth(last.accessor, "initial");
    }
  }, [selectedView, effectiveColumns, setColumnWidth]);

  // Clamp any persisted column width that exceeds XL back down to the max.
  // mantine-datatable's drag handler writes the raw drag width into columnsWidth
  // with no upper bound; this snaps it back. The matching CSS max-width rule in
  // the DataTable sx already caps the live render — this effect only normalizes
  // the persisted state so it doesn't carry a stale > XL value across reloads.
  //
  // Gated on hookInSync (same guard as the additive seeding effect above) so we
  // don't clamp using a stale tab's columnsWidth right after a tab switch.
  //
  // The AI-view elastic last column is intentionally "initial" (handled by the
  // effect above); clampIdeaColumnWidth passes "initial" through unchanged but
  // we also skip the last AI column explicitly for clarity.
  useEffect(() => {
    if (!hookInSync) return;
    if (!columnsWidth || columnsWidth.length === 0) return;

    const aiLastAccessor =
      isAiView(selectedView) && effectiveColumns.length > 0
        ? (effectiveColumns[effectiveColumns.length - 1] as any)?.accessor
        : null;

    const overrides: Record<string, string> = {};
    for (const entry of columnsWidth) {
      for (const accessor of Object.keys(entry)) {
        if (accessor === aiLastAccessor) continue;
        const w = entry[accessor];
        if (exceedsIdeaColumnMaxWidth(w)) {
          overrides[accessor] = `${IDEA_COLUMN_MAX_WIDTH_PX}px`;
        }
      }
    }

    if (Object.keys(overrides).length === 0) return;

    setColumnsWidth((prev) =>
      (prev || []).map((entry: any) => {
        const patch = Object.fromEntries(
          Object.keys(entry)
            .filter((k) => overrides[k] != null)
            .map((k) => [k, overrides[k]])
        );
        return Object.keys(patch).length > 0 ? { ...entry, ...patch } : entry;
      })
    );
  }, [columnsWidth, hookInSync, selectedView, effectiveColumns, setColumnsWidth]);

  // Sync width changes from EditColumnModal (S/M/L/XL) into the hook's stored state.
  // Without this, a previous drag-resize width stored by the hook would override the
  // EditColumnModal's new value.
  const prevWidthsRef = useRef<Record<string, number | string | undefined>>({});
  useEffect(() => {
    const current: Record<string, number | string | undefined> = {};
    for (const col of ideaColumns) {
      if (col.width != null) {
        current[col.accessor] = col.width;
      }
    }

    // Mirror the hookInSync guard used by seeding/clamping. While the hook
    // is out of sync (e.g. right after a tab switch), columnsWidth still
    // reflects the previous tab; spreading it into the new tab's map would
    // corrupt the new tab's persisted widths.
    if (!hookInSync) {
      prevWidthsRef.current = current;
      return;
    }

    const prev = prevWidthsRef.current;
    if (Object.keys(prev).length > 0) {
      // Collect unseeded entries so they can be written in one setColumnsWidth
      // call. Calling it once-per-column inside the loop would let React keep
      // only the last update (stale-snapshot problem), silently dropping the
      // earlier columns' width syncs.
      const unseededEntries: Record<string, string | number>[] = [];

      for (const col of ideaColumns) {
        const accessor = col.accessor;
        if (current[accessor] != null && current[accessor] !== prev[accessor]) {
          const finalAccessor =
            col.renderComponent === "DynamicCell" || col.renderComponent === "BarCell"
              ? updateAccessorByState(accessor, sortState)
              : accessor;
          const clampedValue = clampIdeaColumnWidth(current[accessor]) as string | number;
          const isSeeded = (columnsWidth || []).some((entry: any) => entry[finalAccessor] != null);
          if (isSeeded) {
            setColumnWidth(finalAccessor, clampedValue);
          } else {
            unseededEntries.push({ [finalAccessor]: clampedValue });
          }
        }
      }

      if (unseededEntries.length > 0) {
        // Use a functional updater so this doesn't stomp on a concurrent
        // seeding setColumnsWidth call that may have fired in the same flush.
        setColumnsWidth((prev) => [...(prev || []), ...unseededEntries]);
      }
    }

    prevWidthsRef.current = current;
  }, [ideaColumns, setColumnWidth, setColumnsWidth, columnsWidth, sortState, hookInSync]);

  // Sync column order from ManageColumnsDrawer (arrow reorder) → hook.
  // When the parent updates ideaColumns indices, push that order into the hook
  // so the table reflects the drawer's arrangement.
  const prevIndexOrderRef = useRef<string[]>([]);
  useLayoutEffect(() => {
    const prev = prevIndexOrderRef.current;
    if (prev.length > 0 && JSON.stringify(prev) !== JSON.stringify(defsAccessorOrder)) {
      setColumnsOrder(defsAccessorOrder);
    }
    prevIndexOrderRef.current = defsAccessorOrder;
  }, [defsAccessorOrder, setColumnsOrder]);

  // Sync column order from table drag → parent.
  // When the user drags a column in the table the hook reorders effectiveColumns.
  // Push that new order back to the parent so the ManageColumnsDrawer's list matches.
  // We use _originalAccessor for the parent callback because the parent tracks
  // columns by their raw accessor, not the sortState-transformed one.
  const prevEffectiveOrderRef = useRef<string[]>([]);
  useEffect(() => {
    const effectiveOrder = effectiveColumns.map((c: any) => c.accessor as string);

    // Mantine's `useDataTableColumns` keys its persisted state by `storeKey`,
    // but its internal `useLocalStorage` doesn't immediately re-read when the
    // key changes — for one render after a tab switch, `effectiveColumns`
    // still reflects the previous tab's order/length. Detect that by
    // comparing the SETS of accessors: if they disagree, the hook is stale
    // and pushing its order back to the parent would corrupt the new tab's
    // column state (causes the AI ↔ non-AI flicker loop). The defs→hook sync
    // above will write the correct defs into mantine's storage, and the next
    // render will have matching sets so normal sync can resume.
    const effectiveAccessorSet = new Set(
      effectiveOrder.filter((a) => a != null) as string[]
    );
    const defsAccessorSet = new Set(defsAccessorOrder);
    const setsMatch =
      effectiveAccessorSet.size === defsAccessorSet.size &&
      defsAccessorOrder.every((a) => effectiveAccessorSet.has(a));

    const prev = prevEffectiveOrderRef.current;
    if (
      setsMatch &&
      prev.length > 0 &&
      JSON.stringify(prev) !== JSON.stringify(effectiveOrder) &&
      JSON.stringify(defsAccessorOrder) !== JSON.stringify(effectiveOrder) &&
      onColumnsReordered
    ) {
      const originalOrder = effectiveColumns.map(
        (c: any) => (c._originalAccessor || c.accessor) as string
      );
      onColumnsReordered(originalOrder);
    }
    prevEffectiveOrderRef.current = effectiveOrder;
  }, [effectiveColumns, defsAccessorOrder, onColumnsReordered]);

  const backgroundSxObj = useMemo(() => {
    const sxObj: any = {};
    effectiveColumns.forEach((col: any, index: number) => {
      if (col.customBackground) {
        sxObj[`tbody td:nth-of-type(${index + 1})`] = {
          backgroundColor: col.customBackground,
          "&:hover": { backgroundColor: "#EFEFFD !important" },
        };
      }
    });
    return sxObj;
  }, [effectiveColumns]);

  // When a column header is partially covered by a sticky pinned column
  // (first/last header have z-index 5), Chromium's auto-generated drag
  // image captures the COMPOSITED screen area for the source element —
  // which includes the sticky overlay rendered on top. The result is a
  // drag ghost that visually merges the dragged column with the sticky
  // pinned column. Provide an explicit, off-screen clone as the drag
  // image so the ghost shows only the actual dragged header.
  useEffect(() => {
    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.getAttribute || target.getAttribute("draggable") !== "true") return;
      // Only handle drags that originate inside a mantine-datatable header.
      if (!target.closest(".mantine-Table-root thead")) return;
      if (!e.dataTransfer) return;
      const rect = target.getBoundingClientRect();
      const clone = target.cloneNode(true) as HTMLElement;
      clone.style.position = "fixed";
      clone.style.top = "-10000px";
      clone.style.left = "-10000px";
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.background = "#EFEFFD";
      clone.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
      clone.style.borderRadius = "4px";
      clone.style.pointerEvents = "none";
      clone.style.zIndex = "9999";
      document.body.appendChild(clone);
      const offsetX = Math.min(e.clientX - rect.left, rect.width);
      const offsetY = Math.min(e.clientY - rect.top, rect.height);
      e.dataTransfer.setDragImage(clone, offsetX, offsetY);
      // Browser captures the drag image synchronously; remove the clone
      // on the next frame so it doesn't stay in the DOM.
      requestAnimationFrame(() => {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      });
    };
    document.addEventListener("dragstart", handleDragStart, true);
    return () => document.removeEventListener("dragstart", handleDragStart, true);
  }, []);

  // Hard-stop drag resize at XL via a capture-phase mousemove listener that
  // fires before mantine's bubble-phase handler. When clientX - th.left > XL,
  // stopImmediatePropagation prevents mantine from writing an over-max width.
  useEffect(() => {
    let activeResizeTh: HTMLTableCellElement | null = null;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const th = target.closest("th") as HTMLTableCellElement | null;
      if (!th) return;
      if (getComputedStyle(th).cursor !== "col-resize") return;
      activeResizeTh = th;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!activeResizeTh || document.body.style.cursor !== "col-resize") return;
      const rect = activeResizeTh.getBoundingClientRect();
      // Equivalent to mantine's: rect.width + (clientX - rect.right) = clientX - rect.left
      const projectedWidth = e.clientX - rect.left;
      if (projectedWidth > IDEA_COLUMN_MAX_WIDTH_PX) {
        // Prevent mantine's bubble-phase handler from computing or persisting
        // a width above the max.
        e.stopImmediatePropagation();
        // Ensure the inline style is clamped so getBoundingClientRect() is
        // stable on the next event (prevents the column from visually jumping).
        if (activeResizeTh.style.width !== `${IDEA_COLUMN_MAX_WIDTH_PX}px`) {
          activeResizeTh.style.width = `${IDEA_COLUMN_MAX_WIDTH_PX}px`;
        }
      }
    };

    const handleMouseUp = () => {
      activeResizeTh = null;
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("mousemove", handleMouseMove, true);
    document.addEventListener("mouseup", handleMouseUp, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("mousemove", handleMouseMove, true);
      document.removeEventListener("mouseup", handleMouseUp, true);
    };
  }, []);

  return (
    <DataTable
      pinLastColumn
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        thead: {
          position: "sticky",
          top: 0,
          zIndex: 4,
          backgroundColor: "#F3F4F6",
        },
        "thead tr:first-of-type": {
          borderColor: "#dee2e6",
          fontSize: 12,
        },
        "thead th": {
          height: "40px",
          minHeight: "40px",
          padding: "0 !important",
          fontSize: 12,
          textAlign: "left",
          verticalAlign: "middle",
          backgroundColor: "#EFEFFD",
          transition: "background-color 0.15s ease",
          // Hard cap on user-resizable / configurable column width. Caps the
          // visual width during a live drag (mantine-datatable's handler sets
          // style.width inline; max-width clamps the rendered width regardless),
          // and survives the double-click reset which sets style.maxWidth=initial
          // inline thanks to !important. The :last-of-type override below
          // exempts the sticky/elastic pinned column.
          maxWidth: `${IDEA_COLUMN_MAX_WIDTH_PX}px !important`,
          "&:hover": {
            backgroundColor: "#E0E0F5 !important",
          },
          "&:has([data-expanded])": {
            backgroundColor: "#D0D4F5 !important",
          },
        },
        "tbody td": {
          height: "48px",
          fontSize: 12,
          overflow: "hidden",
          maxWidth: `${IDEA_COLUMN_MAX_WIDTH_PX}px !important`,
        },
        "tbody td > *": {
          maxWidth: "100%",
          minWidth: 0,
        },
        ".external-link-icon": {
          display: "none",
          opacity: 0,
          transition: "opacity 0.2s ease",
        },
        "tbody tr:hover .external-link-title": {
          opacity: 1,
          display: "flex",
        },
        ".external-link-title": {
          display: "none",
        },
        ".text-cell-title": {
          position: "relative",
        },
        "thead th:first-of-type": {
          position: "sticky",
          left: 0,
          zIndex: 5,
          backgroundColor: "#EFEFFD",
          borderRight: "1px solid #dee2e6",
          "&:hover": { backgroundColor: "#E0E0F5 !important" },
          "&:has([data-expanded])": { backgroundColor: "#D0D4F5 !important" },
        },
        "thead th:last-of-type": {
          position: "sticky",
          right: 0,
          zIndex: 5,
          backgroundColor: "#EFEFFD",
          borderLeft: "1px solid #dee2e6 !important",
          // On the Bagel AI tab the last column is the elastic absorber
          // (width: "initial") that fills leftover space — lift the XL cap so
          // it can stretch freely. On all other tabs the last column is a
          // normal user column that must obey the same XL cap as all others.
          maxWidth: isAiView(selectedView) ? "none !important" : `${IDEA_COLUMN_MAX_WIDTH_PX}px !important`,
          "&:hover": { backgroundColor: "#E0E0F5 !important" },
          "&:has([data-expanded])": { backgroundColor: "#D0D4F5 !important" },
        },
        "tbody td:first-of-type": {
          position: "sticky",
          left: 0,
          zIndex: 3,
          backgroundColor: "white",
          borderRight: "1px solid #dee2e6",
          "&:hover": {
            backgroundColor: "#EFEFFD !important",
          },
        },

        "tbody tr[data-state='generated'] td:first-of-type": {
          backgroundColor: isAiView(selectedView) ? 'white' : "#FFFCE6",
        },
        "tbody td:last-of-type": {
          backgroundColor: "white",
          borderLeft: "1px solid #dee2e6 !important",
          maxWidth: isAiView(selectedView) ? "none !important" : `${IDEA_COLUMN_MAX_WIDTH_PX}px !important`,
          /* "&:hover": {
              backgroundColor: "#dbe8ff !important", 
            }, */
        },
        "tbody tr": {
          "&:hover": {
            backgroundColor: !isTagsHovered
              ? "#EFEFFD !important"
              : "white !important", // Row hover background color
            "& td:first-of-type": {
              backgroundColor: !isTagsHovered
                ? "#EFEFFD !important"
                : "white !important", // First column hover color
            },
            "& td:last-of-type": {
              backgroundColor: !isTagsHovered
                ? "#EFEFFD !important"
                : "white !important", // Last column hover color
            },
            "& td": {
              backgroundColor: "inherit !important", // Ensure all cells follow the row hover style
            },
          },
        },
        ...backgroundSxObj,
      }}
      sortStatus={sortStatus}
      withBorder
      idAccessor={"_id"}
      storeColumnsKey={storeKey}
      records={loading ? skeletonData : ideas}
      sortIcons={{
        sorted: <SortArrow />,
        unsorted: <div/>
      }}
      columns={effectiveColumns}
      rowStyle={(record: any) => {

        if(isAiView(selectedView) && generatedEnabled){
          return undefined;
        }
        else if (
          record.state === "generated" &&
            generatedEnabled 
        ) {
          return { backgroundColor: "#FFFCE6" };
        }
        return undefined;
      }}
      customRowAttributes={({ state }) => ({
        "data-state": state
      })}
      height={`calc(100vh - 200px)`}
      totalRecords={totalRecords}
      recordsPerPage={recordsPerPage}
      // While a query is in flight totalRecords is 0, so the footer falls through
      // to noRecordsText. Carrying the loading copy here rather than via the
      // library's `fetching` prop keeps the skeleton rows as the only loading
      // treatment — `fetching` would also raise an opaque loader overlay that the
      // library exposes no way to style away.
      noRecordsText={loading ? 'Loading results…' : 'No records'}
      page={page}
      onPageChange={(p) => handlePageChange(p.toString())}
      onCellClick={({ record: idea, column }: any) => {
        const rc =
          column?.renderComponent ?? column?.ideaTableRenderComponent;

        if (rc === "TitleLinkCell") {
          const parentId =
            idea?.parentIdeaId ?? idea?.parentIdea?._id;
          if (parentId) {
            handleOnRowClick(parentId);
            return;
          }
          // No parent — fall through to normal row-click so the idea still opens
        }

        if (
          column?.openIdeaOnCLick === undefined ||
          column?.openIdeaOnCLick
        ) {
          const isStatusAccessor =
            column?.accessor === "status" ||
            column?._originalAccessor === "status";
          if (
            rc === "StarCell" ||
            rc === "OwnerCell" ||
            rc === "StatusCell" ||
            // Editing releases in place must not also navigate into the idea.
            rc === "ReleaseCell" ||
            isStatusAccessor
          ) {
            return;
          } else {
            handleOnRowClick(idea._id);
          }
        }
      }}
    />
  );
}
